import { useEffect, useState } from 'react';
import { collection, query, where, onSnapshot, orderBy, limit } from 'firebase/firestore';
import { db } from 'src/config/firebase';
import { sessionStore } from 'src/store/sessionStore';
import { ChatConversationItem } from 'src/api/mutations/useGetTrainerMessages';

/**
 * Normalizes raw Firestore chat document to the standard ChatConversationItem shape
 * so that realtime data matches the API data exactly.
 */
function normalizeFirestoreChat(
  docData: any,
  chatId: string,
  trainerUid: string,
): ChatConversationItem | null {
  if (!docData) return null;
  const participants = docData.participants || [];
  if (!participants.includes(trainerUid)) return null;

  // client is the other participant
  const clientUid = participants.find((p: string) => p !== trainerUid);
  if (!clientUid) return null;

  const rawLastMsg = docData.last_message;
  let bodyStr = '';
  let msgType = 'text';
  let senderUid = '';
  let fullName = 'Client';
  let profilePicture = null;

  if (typeof rawLastMsg === 'string') {
    bodyStr = rawLastMsg;
  } else if (rawLastMsg && typeof rawLastMsg === 'object') {
    bodyStr = rawLastMsg.body || '';
    msgType = rawLastMsg.message_type || 'text';
    senderUid = rawLastMsg.sender_uid || '';
    if (rawLastMsg.sender_uid === clientUid) {
      fullName = rawLastMsg.sender_name || fullName;
      profilePicture = rawLastMsg.sender_avatar || profilePicture;
    } else if (rawLastMsg.receive_uid === clientUid) {
      fullName = rawLastMsg.receive_name || fullName;
      profilePicture = rawLastMsg.receive_avatar || profilePicture;
    }
  }

  const unreadCount = docData[`unread_count_${trainerUid}`] || 0;
  const lastMessageTime = docData.last_message_time || docData.update_time;

  let sendTimeIso = new Date().toISOString();
  if (lastMessageTime) {
    if (typeof lastMessageTime.toDate === 'function') {
      sendTimeIso = lastMessageTime.toDate().toISOString();
    } else if (typeof lastMessageTime === 'string') {
      sendTimeIso = new Date(lastMessageTime).toISOString();
    } else if (typeof lastMessageTime === 'number') {
      sendTimeIso = new Date(lastMessageTime).toISOString();
    }
  }

  const isActive = docData.is_active !== undefined ? Boolean(docData.is_active) : true;
  const isUnbound = Boolean(docData.is_unbound);

  return {
    chatId,
    client: {
      uid: clientUid,
      fullName,
      profilePicture,
    },
    lastMessage: {
      body: bodyStr,
      messageType: msgType,
      senderUid,
      sendTime: sendTimeIso,
    },
    unreadCount,
    isUnread: unreadCount > 0,
    isActive,
    isUnbound,
  };
}

export function useChatRealtime() {
  const currentUser = sessionStore((s) => s.currentUser);
  const trainerId = currentUser?.uid;

  // We store the current realtime updates in a map to easily merge
  const [realtimeUpdates, setRealtimeUpdates] = useState<Record<string, ChatConversationItem>>({});

  // To avoid unneeded re-renders when setting state, track unread total manually if needed
  const [totalUnread, setTotalUnread] = useState(0);

  useEffect(() => {
    if (!trainerId) return;

    const chatsRef = collection(db, 'flutter_chat_messages');
    const q = query(
      chatsRef,
      where('participants', 'array-contains', trainerId),
      orderBy('last_message_time', 'desc'),
      limit(100),
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const updates: Record<string, ChatConversationItem> = {};
        let currentUnread = 0;

        snapshot.docs.forEach((doc) => {
          const data = doc.data();
          const normalized = normalizeFirestoreChat(data, doc.id, trainerId);
          if (normalized) {
            updates[doc.id] = normalized;
            currentUnread += normalized.unreadCount;
          }
        });

        setRealtimeUpdates(updates);
        setTotalUnread(currentUnread);
      },
      (err) => {
        console.error('Error in realtime chat listener:', err);
      },
    );

    return () => unsubscribe();
  }, [trainerId]);

  return { realtimeUpdates, totalUnread };
}
